import { CompetenceType, EditGroup, ParticipantsPayloadType, SectionGroup } from '../../components/survey/types'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import appAPI from '../../api/api'
import {
  ParticipantsSurveyItemType,
  ParticipantsSurveyType,
} from '../../components/survey/common/participants-list/participants-list'

type State = {
  groups: DataLoad<SectionGroup[]>
  editGroup: EditGroup
  editGroupLoading: boolean
  editGroupRequestId: string
  participants: DataLoad<ParticipantsSurveyType>
  participantsRequestKey: string
  participantsRequestId: string
}

export const initialState: State = {
  groups: { loading: false, data: [] },
  editGroup: { id: 0, name: '', competences: [], Members: [] },
  editGroupLoading: false,
  editGroupRequestId: '',
  participants: { loading: false, data: { results: [], total: 0, page: 0 } },
  participantsRequestKey: '',
  participantsRequestId: '',
}

type FetchParticipantsPayload = ParticipantsPayloadType & {
  requestKey: string
}

const getFullName = (x: any) =>
  `${x.lastName ? x.lastName : x.username} ${x.firstName ?? ''} ${x.middleName ?? ''}`.trim()

const normalizeParticipants = (items: any[]): ParticipantsSurveyItemType[] =>
  items.map((x: any) => ({
    ...x,
    fullName: getFullName(x),
  }))

const fetchGroup = createAsyncThunk('fetchGroupAdmin', (payload: Paginated) => {
  return appAPI.groups.get(payload)
})

const fetchParticipants = createAsyncThunk(
  'fetchParticipantsGroup',
  async (payload: FetchParticipantsPayload, { signal }) => {
    const { requestKey, ...requestPayload } = payload
    const cancelSource = axios.CancelToken.source()
    const abortRequest = () => cancelSource.cancel('Participants request was superseded')

    signal.addEventListener('abort', abortRequest)

    try {
      return await appAPI.groups.getParticipants(requestPayload, cancelSource.token)
    } finally {
      signal.removeEventListener('abort', abortRequest)
    }
  },
)

const updateGroup = createAsyncThunk('updateGroup', (payload: EditGroup) => {
  const output: ApiEditGroup = {
    id: payload.id,
    name: payload.name,
    competences: payload.competences,
    Members: payload.Members.map((x) => {
      return {
        id: x.id,
        fullName: '',
        avatar: null,
        competence: {
          id: 0,
          name: '',
          pictogram: '',
          description: '',
        },
        role: '',
        company: '',
        position: '',
      }
    }),
  }

  if (payload.id === 0) return appAPI.groups.create(output)

  return appAPI.groups.update(output)
})

const fetchDetailGroup = createAsyncThunk('fetchDetailGroupAdmin', (payload: number) => {
  return appAPI.groups.getDetail(payload)
})

const deleteGroup = createAsyncThunk('deleteGroupAdmin', (id: number) => {
  return appAPI.groups.deleteGroup(id)
})

const group = createSlice({
  name: 'sectionGroupSlice',
  initialState,
  reducers: {
    clearGroups: (state) => {
      state.groups = initialState.groups
    },

    clearFullGroups: (state) => {
      state.editGroup = initialState.editGroup
      state.editGroupLoading = initialState.editGroupLoading
      state.editGroupRequestId = initialState.editGroupRequestId
      state.participants = initialState.participants
      state.participantsRequestKey = initialState.participantsRequestKey
      state.participantsRequestId = initialState.participantsRequestId
    },

    clearParticipants: (state) => {
      state.participants = initialState.participants
      state.participantsRequestKey = initialState.participantsRequestKey
      state.participantsRequestId = initialState.participantsRequestId
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchGroup.pending, (state) => {
      state.groups.loading = true
    })

    builder.addCase(fetchGroup.fulfilled, (state, { payload }) => {
      state.groups.loading = false
      state.groups.data = state.groups.data.concat(payload.data)
    })

    builder.addCase(fetchGroup.rejected, (state) => {
      state.groups.loading = false
    })

    builder.addCase(fetchDetailGroup.pending, (state, { meta }) => {
      state.editGroup = initialState.editGroup
      state.editGroupLoading = true
      state.editGroupRequestId = meta.requestId
    })

    builder.addCase(fetchDetailGroup.fulfilled, (state, { payload, meta }) => {
      if (state.editGroupRequestId !== meta.requestId) return

      state.editGroup = payload.data
      state.editGroupLoading = false
      state.editGroupRequestId = ''
    })

    builder.addCase(fetchDetailGroup.rejected, (state, { meta }) => {
      if (state.editGroupRequestId !== meta.requestId) return

      state.editGroup = initialState.editGroup
      state.editGroupLoading = false
      state.editGroupRequestId = ''
    })

    builder.addCase(updateGroup.fulfilled, (state, { payload }) => {
      state.editGroup = payload.data
    })

    builder.addCase(deleteGroup.fulfilled, (state, payload) => {
      state.groups.data = state.groups.data.filter((x) => x.id !== payload.meta.arg)
    })

    builder.addCase(fetchParticipants.pending, (state, { meta }) => {
      state.participants.loading = true
      const offset = meta.arg.offset ?? 0

      if (offset === 0) {
        state.participants.data = initialState.participants.data
      }
      state.participantsRequestKey = meta.arg.requestKey
      state.participantsRequestId = meta.requestId
    })

    builder.addCase(fetchParticipants.fulfilled, (state, { payload, meta }) => {
      if (state.participantsRequestKey !== meta.arg.requestKey || state.participantsRequestId !== meta.requestId) {
        return
      }

      const items = Array.isArray(payload?.data.results) ? payload.data.results : []
      const normalizedItems = normalizeParticipants(items)
      const offset = meta.arg.offset ?? 0

      state.participants.loading = false
      if (offset === 0) {
        state.participants.data.results = normalizedItems
      } else {
        const participantsById = new Map(
          state.participants.data.results.map((participant) => [participant.id, participant]),
        )
        normalizedItems.forEach((participant) => participantsById.set(participant.id, participant))
        state.participants.data.results = Array.from(participantsById.values())
      }
      state.participants.data.total = payload?.data.total ?? state.participants.data.results.length
      state.participants.data.page = payload?.data.page ?? state.participants.data.page
      state.participantsRequestId = ''
    })

    builder.addCase(fetchParticipants.rejected, (state, { meta }) => {
      if (state.participantsRequestKey !== meta.arg.requestKey || state.participantsRequestId !== meta.requestId) {
        return
      }

      state.participants.loading = false
      state.participantsRequestId = ''
      if ((meta.arg.offset ?? 0) === 0 && !meta.aborted) {
        state.participants.data = initialState.participants.data
      }
    })
  },
})

export default group.reducer

export const groupActions = {
  ...group.actions,
  fetchGroup,
  deleteGroup,
  fetchDetailGroup,
  updateGroup,
  fetchParticipants,
}

export type ApiEditGroup = {
  id: number
  name: string
  competences: number[]
  Members: ApiMembers[]
}

type ApiMembers = {
  id: number
  fullName: string
  avatar: null
  competence: CompetenceType
  role: string
  company: string
  position: string
}
